import { Flex, Box, Table, Tbody, Td, Text, Th, Thead, Tr, useColorModeValue, Badge, IconButton, Tooltip, VStack, HStack, Icon, SimpleGrid, Select } from '@chakra-ui/react';
import * as React from 'react';
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import Card from "../../../../components/card/Card";
import { ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { MdExpandMore, MdExpandLess, MdVolunteerActivism } from 'react-icons/md';
import { orderService } from '../../../../services/orderService';

const columnHelper = createColumnHelper();

export default function DoneOrdersTable({ completedOrders = [], isLoading, onTimeRangeChange }) {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const bgColor = useColorModeValue('white', 'navy.800');
  const hoverBg = useColorModeValue('gray.50', 'navy.700');
  
  const [data, setData] = React.useState([]);
  const [pageIndex, setPageIndex] = React.useState(0);
  const [expandedRows, setExpandedRows] = React.useState(new Set());
  const [timeRange, setTimeRange] = React.useState('today');
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const pageSize = 10;

  // Veriyi sipariş bazında formatla
  React.useEffect(() => {
    if (Array.isArray(completedOrders)) {
      const formattedData = completedOrders.map(item => ({
          id: item.id,
          masa_id: item.tableName || item.tableId?.name || 'Masa Bilgisi Yok',
        userName: item.userName || 'Misafir',
        items: item.items || [],
        totalAmount: item.totalAmount || 0,
        menomCoinsUsed: item.menomCoinsUsed || 0,
        tipAmount: item.tipAmount || 0,
        tipPercent: item.tipPercent || null,
          date: item.createdAt,
          status: item.status,
          isFromYesterday: item.isFromYesterday,
        notes: item.notes || '',
        paymentMethod: item.paymentMethod || 'restaurant',
        deliveredBy: item.deliveredBy || null
      }));

      // Tarihe göre sırala (en yeni önce)
      formattedData.sort((a, b) => new Date(b.date) - new Date(a.date));
      setData(formattedData);
      setPageIndex(0); // Filtre değiştiğinde sayfayı sıfırla
    }
  }, [completedOrders]);

  // Zaman aralığı değiştiğinde siparişleri yeniden yükle
  const handleTimeRangeChange = async (newTimeRange) => {
    setTimeRange(newTimeRange);
    setIsRefreshing(true);
    if (onTimeRangeChange) {
      await onTimeRangeChange(newTimeRange);
    }
    setIsRefreshing(false);
  };

  const toggleRow = (orderId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
    }
      return newSet;
    });
  };

  const getStatusColor = (status) => {
    return status === 'Teslim Edildi' ? 'green' : 'red';
  };

  const columns = [
    columnHelper.accessor('masa_id', {
    header: () => <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="700">Masa No</Text>,
      cell: (info) => {
        const isExpanded = expandedRows.has(info.row.original.id);
        return (
                  <Flex align="center" gap={2}>
            <Icon
              as={isExpanded ? MdExpandLess : MdExpandMore}
              color="gray.500"
              boxSize={{ base: "16px", md: "20px" }}
              cursor="pointer"
            />
            <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="700">{info.getValue()}</Text>
                </Flex>
        );
      },
    }),
    columnHelper.accessor('userName', {
      header: () => <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="700">Sipariş Veren</Text>,
      cell: (info) => <Text fontSize={{ base: "xs", md: "sm" }}>{info.getValue()}</Text>,
    }),
    columnHelper.accessor('totalAmount', {
      header: () => <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="700">Toplam Tutar</Text>,
      cell: (info) => {
        const order = info.row.original;
        const hasMenomCoins = order.menomCoinsUsed > 0;
        return (
          <VStack align="start" spacing={1}>
            {hasMenomCoins && (
              <Text 
                color="gray.500" 
                fontSize={{ base: "2xs", md: "xs" }} 
                textDecoration="line-through"
              >
                {((order.totalAmount + order.menomCoinsUsed).toFixed(2))} ₺
              </Text>
            )}
            <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="700">
              {info.getValue().toFixed(2)} ₺
            </Text>
          </VStack>
        );
      },
    }),
    columnHelper.accessor('date', {
      header: () => <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="700">Tarih ve Saat</Text>,
      cell: (info) => {
        const date = new Date(info.getValue());
        return (
          <Text fontSize={{ base: "xs", md: "sm" }} whiteSpace="nowrap">
            {date.toLocaleDateString('tr-TR')} {date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
          </Text>
        );
      },
    }),
    columnHelper.accessor('status', {
      header: () => <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="700">Durum</Text>,
      cell: (info) => (
        <Badge 
          colorScheme={getStatusColor(info.getValue())}
          variant="solid"
          p={{ base: 1, md: 2 }}
          borderRadius="8px"
          fontSize={{ base: "xs", md: "sm" }}
        >
          {info.getValue()}
        </Badge>
      ),
    }),
  ];

  const currentPageData = React.useMemo(() => {
    const start = pageIndex * pageSize;
    const end = start + pageSize;
    return data.slice(start, end);
  }, [data, pageIndex, pageSize]);

  const pageCount = Math.ceil(data.length / pageSize);

  const table = useReactTable({
    data: currentPageData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading) {
    return (
      <Card>
        <Flex justify="center" align="center" p={8}>
          <Text>Yükleniyor...</Text>
        </Flex>
      </Card>
    );
  }

  return (
    <Card flexDirection="column" w="100%">
      <Flex 
        px={{ base: "15px", md: "25px" }} 
        mb="8px" 
        justifyContent="space-between" 
        align="center"
        flexDirection={{ base: "column", sm: "row" }}
        gap={{ base: 3, sm: 0 }}
      >
        <Box>
          <Text color={textColor} fontSize={{ base: "18px", md: "22px" }} fontWeight="700">
            Tamamlanmış Siparişler
          </Text>
        </Box>
        <Select
          value={timeRange}
          onChange={(e) => handleTimeRangeChange(e.target.value)}
          width={{ base: "100%", sm: "200px" }}
          size="sm"
          isDisabled={isRefreshing}
        >
          <option value="today">Bugün</option>
          <option value="yesterday">Dün</option>
          <option value="week">Son 1 Hafta</option>
          <option value="month">Son 1 Ay</option>
        </Select>
      </Flex>
      <Box overflowX="auto">
        <Table variant="simple" color="gray.500" mb="24px" mt="12px" minW={{ base: "600px", md: "auto" }}>
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th 
                    key={header.id} 
                    colSpan={header.colSpan} 
                    borderColor={borderColor}
                    px={{ base: 2, md: 4 }}
                    py={{ base: 2, md: 3 }}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {currentPageData.length === 0 ? (
              <Tr>
                <Td colSpan={columns.length}>
                  <Text textAlign="center">Henüz tamamlanmış sipariş yok.</Text>
                </Td>
              </Tr>
            ) : (
              table.getRowModel().rows.map((row) => {
                const order = row.original;
                const isExpanded = expandedRows.has(order.id);
                
                return (
                  <React.Fragment key={row.id}>
                    <Tr 
                      cursor="pointer"
                      onClick={() => toggleRow(order.id)}
                      bg={isExpanded ? hoverBg : bgColor}
                      _hover={{ bg: hoverBg }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <Td 
                      key={cell.id} 
                      borderColor="transparent"
                      px={{ base: 2, md: 4 }}
                      py={{ base: 2, md: 3 }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Td>
                  ))}
                </Tr>
                    {isExpanded && (
                      <Tr>
                        <Td colSpan={columns.length} p={0} borderColor={borderColor}>
                          <Box bg={bgColor} p={{ base: 3, md: 4 }} borderTop="1px solid" borderColor={borderColor}>
                            <VStack align="stretch" spacing={4}>
                              {/* Sipariş Detayları */}
                              <Box>
                                <Text fontWeight="bold" fontSize={{ base: "sm", md: "md" }} color={textColor} mb={3}>
                                  Sipariş Detayları
                                </Text>
                                
                                {/* Ürünler - 1'den fazla varsa yan yana */}
                                {order.items.length > 1 ? (
                                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mb={4}>
                                    {order.items.map((item, idx) => {
                                      const hasModifications = 
                                        (item.removedIngredients?.length > 0) || 
                                        (item.selectedOptions?.length > 0) ||
                                        (item.selectedExtras?.length > 0); 
                                      
                                      return (
                                        <Box
                                          key={idx}
                                          p={3}
                                          bg={hoverBg}
                                          borderRadius="md"
                                          borderLeft="3px solid"
                                          borderColor={hasModifications ? "blue.400" : "gray.300"}
                                        >
                                          <Flex justify="space-between" align="start" mb={2}>
                                            <VStack align="start" spacing={1}>
                                              <Text fontWeight="bold" fontSize={{ base: "xs", md: "sm" }} color={textColor}>
                                                {item.name} x{item.quantity}
                                              </Text>
                                              <Text fontSize={{ base: "xs", md: "sm" }} color="gray.600">
                                                {item.price.toFixed(2)} ₺ x {item.quantity} = {(item.price * item.quantity).toFixed(2)} ₺
                                              </Text>
                                            </VStack>
                                          </Flex>
                                          
                                          {item.selectedOptions?.length > 0 && (
                                            <Flex align="center" gap={2} mt={2}>
                                              <Box w="2" h="2" borderRadius="full" bg="blue.500" />
                                              <Text color="blue.600" fontSize="xs" fontWeight="500">
                                                Seçenekler: {item.selectedOptions.map(option => {
                                                  const name = option.name || option.optionName || '';
                                                  const choiceName = option.choiceName || option.value || '';
                                                  const price = option.price || 0;
                                                  const priceText = price > 0 ? ` (+${price}₺)` : '';
                                                  return `${name}: ${choiceName}${priceText}`;
                                                }).join(', ')}
                                              </Text>
                                            </Flex>
                                          )}

                                          {item.removedIngredients?.length > 0 && (
                                            <Flex align="center" gap={2} mt={2}>
                                              <Box w="2" h="2" borderRadius="full" bg="red.500" />
                                              <Text color="red.500" fontSize="xs">
                                                Çıkarılanlar: {item.removedIngredients.join(', ')}
                                              </Text>
                                            </Flex>
                                          )}
                                          
                                          {item.selectedExtras?.length > 0 && (
                                            <Flex align="center" gap={2} mt={2}>
                                              <Box w="2" h="2" borderRadius="full" bg="green.500" />
                                              <Text color="green.500" fontSize="xs">
                                                Ekstralar: {item.selectedExtras.map(extra => 
                                                  `${extra.name} (+${extra.price}₺)`
                                                ).join(', ')}
                                              </Text>
                                            </Flex>
                                          )}
                                        </Box>
                                      );
                                    })}
                                  </SimpleGrid>
                                ) : (
                                  <VStack align="stretch" spacing={2} mb={4}>
                                    {order.items.map((item, idx) => {
                                      const hasModifications = 
                                        (item.removedIngredients?.length > 0) || 
                                        (item.selectedExtras?.length > 0);
                                      
                                      return (
                                        <Box
                                          key={idx}
                                          p={3}
                                          bg={hoverBg}
                                          borderRadius="md"
                                          borderLeft="3px solid"
                                          borderColor={hasModifications ? "blue.400" : "gray.300"}
                                        >
                                          <Flex justify="space-between" align="start" mb={2}>
                                            <VStack align="start" spacing={1}>
                                              <Text fontWeight="bold" fontSize={{ base: "xs", md: "sm" }} color={textColor}>
                                                {item.name} x{item.quantity}
                                              </Text>
                                              <Text fontSize={{ base: "xs", md: "sm" }} color="gray.600">
                                                {item.price.toFixed(2)} ₺ x {item.quantity} = {(item.price * item.quantity).toFixed(2)} ₺
                                              </Text>
                                            </VStack>
                                          </Flex>
                                          
                                          {item.removedIngredients?.length > 0 && (
                                            <Flex align="center" gap={2} mt={2}>
                                              <Box w="2" h="2" borderRadius="full" bg="red.500" />
                                              <Text color="red.500" fontSize="xs">
                                                Çıkarılanlar: {item.removedIngredients.join(', ')}
                                              </Text>
                                            </Flex>
                                          )}
                                          
                                          {item.selectedExtras?.length > 0 && (
                                            <Flex align="center" gap={2} mt={2}>
                                              <Box w="2" h="2" borderRadius="full" bg="green.500" />
                                              <Text color="green.500" fontSize="xs">
                                                Ekstralar: {item.selectedExtras.map(extra => 
                                                  `${extra.name} (+${extra.price}₺)`
                                                ).join(', ')}
                                              </Text>
                                            </Flex>
                                          )}
                                        </Box>
                                      );
                                    })}
                                  </VStack>
                                )}

                                {/* Sipariş Notu */}
                                {order.notes && (
                                  <Box
                                    p={3}
                                    bg="yellow.50"
                                    borderRadius="md"
                                    border="1px solid"
                                    borderColor="yellow.200"
                                    mb={4}
                                  >
                                    <Flex align="center" gap={2}>
                                      <Box w="2" h="2" borderRadius="full" bg="yellow.500" />
                                      <Text color="yellow.700" fontSize="sm" fontWeight="medium">
                                        Sipariş Notu: {order.notes}
                                      </Text>
                                    </Flex>
                                  </Box>
                                )}

                                {/* Ödeme Bilgileri ve Teslim Eden - Yan Yana */}
                                <HStack spacing={4} mb={4} flexWrap="wrap">
                                  <Box>
                                    <Text fontSize="xs" color="gray.500" mb={1}>Ödeme Yöntemi</Text>
                                    <Badge 
                                      colorScheme={order.paymentMethod === 'app' ? 'green' : 'red'}
                                      variant="solid"
                                      p={1}
                                      borderRadius="4px"
                                      fontSize="xs"
                                    >
                                      {order.paymentMethod === 'app' ? 'Online Ödeme' : 'Nakit Ödeme'}
                                    </Badge>
                                  </Box>
                                  
                                  {order.menomCoinsUsed > 0 && (
                                    <Box>
                                      <Text fontSize="xs" color="gray.500" mb={1}>MenomCoin Kullanıldı</Text>
                                      <Text fontSize="sm" fontWeight="bold" color="yellow.500">
                                        {order.menomCoinsUsed} MenomCoin
                                      </Text>
                                    </Box>
                                  )}

                                  {order.tipAmount > 0 && (
                                    <Box>
                                      <Text fontSize="xs" color="gray.500" mb={1}>Bahşiş</Text>
                                      <HStack spacing={1}>
                                        <Icon as={MdVolunteerActivism} color="green.500" />
                                        <Text fontSize="sm" fontWeight="bold" color="green.500">
                                          {order.tipAmount.toFixed(2)} ₺
                                        </Text>
                                        {order.tipPercent && (
                                          <Text fontSize="xs" color="gray.500">
                                            (%{order.tipPercent})
                                          </Text>
                                        )}
                                      </HStack>
                                    </Box>
                                  )}

                                  {/* Teslim Eden Çalışan */}
                                  {order.deliveredBy && (
                                    <Box>
                                      <Text fontSize="xs" color="gray.500" mb={1}>
                                        Siparişi Teslim Eden
                                      </Text>
                                      <Text fontSize="sm" fontWeight="bold" color="green.700">
                                        {order.deliveredBy.name} - {order.deliveredBy.position}
                                      </Text>
                                    </Box>
                                  )}
                                    </HStack>
                                  </Box>
                                </VStack>
                              </Box>
                            </Td>
                          </Tr>
                        )}
                  </React.Fragment>
                );
              })
            )}
          </Tbody>
        </Table>
      </Box>
      {data.length > 0 && (
        <Flex 
          w="100%" 
          justify="space-between" 
          align="center" 
          px={{ base: "15px", md: "25px" }} 
          py="10px"
          flexDirection={{ base: "column", sm: "row" }}
          gap={{ base: 3, sm: 0 }}
        >
          <Text fontSize={{ base: "2xs", md: "xs" }} color="gray.500" fontWeight="normal" textAlign={{ base: "center", sm: "left" }}>
            Toplam {data.length} sipariş içinden {pageIndex * pageSize + 1} - {Math.min((pageIndex + 1) * pageSize, data.length)} arası gösteriliyor
          </Text>
          <Flex gap={2} align="center">
            <IconButton
              onClick={() => setPageIndex(old => Math.max(0, old - 1))}
              isDisabled={pageIndex === 0}
              icon={<ChevronLeftIcon />}
              size={{ base: "xs", md: "sm" }}
              variant="outline"
              aria-label="Önceki sayfa"
            />
            <Text fontSize={{ base: "2xs", md: "xs" }} color="gray.500" fontWeight="normal">
              Sayfa {pageIndex + 1} / {pageCount}
            </Text>
            <IconButton
              onClick={() => setPageIndex(old => Math.min(pageCount - 1, old + 1))}
              isDisabled={pageIndex === pageCount - 1}
              icon={<ChevronRightIcon />}
              size={{ base: "xs", md: "sm" }}
              variant="outline"
              aria-label="Sonraki sayfa"
            />
          </Flex>
        </Flex>
      )}
    </Card>
  );
}
